// ============================================================
// assetStore - the asset library's reads and writes, through `backend` only.
//
// Everything lives under `owners/{uid}/…` (multiplayer.md, "The asset
// library"): the path proves the owner on both backends. The page WATCHES
// only the small rows (folders, assets); a version's content, its parts and
// the card's picture are read on demand.
//
// A save is four writes, in this order (engine/assets/versions.ts says why):
//   1. reserve the next number on the asset row (compare-and-set);
//   2. the content's parts;
//   3. the version's facts - after the parts, so facts mean "whole";
//   4. commit the row onto it (compare-and-set again).
// An image or a handout (stage 3) is the same four steps with a FILE for
// step 2: its document `assetFiles/v0007` first (both servers refuse a file on
// a document that is not there), then the bytes through the files port.
// A save that stops part-way leaves the row on its last whole version and
// some orphan parts, which `reapOrphans` takes once they are old enough.
//
// Every failure throws a sentence the page shows; nothing is swallowed.
// ============================================================
import { backend } from './backend/app';
import { splitText, joinParts, type Part } from './engine/storage/parts';
import {
  assetFromDoc,
  bytesOfDoc,
  docFromAsset,
  folderFromDoc,
  type AssetDoc,
  type FolderDoc,
} from './engine/assets/rows';
import { commit, movedPast, orphanVersions, partId, pruned, reserve, versionId } from './engine/assets/versions';
import { isFileKind, type AssetRow, type FolderRow, type Note, type VersionMeta } from './engine/assets/types';
import { extensionFor } from './engine/assets/fileKinds';
import type { Unsubscribe } from './backend/types';

const ownerRoot = (uid: string) => `owners/${uid}`;
const foldersPath = (uid: string) => `${ownerRoot(uid)}/assetFolders`;
const assetsPath = (uid: string) => `${ownerRoot(uid)}/assets`;
const assetPath = (uid: string, id: string) => `${assetsPath(uid)}/${id}`;
const versionsPath = (uid: string, id: string) => `${assetPath(uid, id)}/assetVersions`;
const partsPath = (uid: string, id: string) => `${assetPath(uid, id)}/assetParts`;
const thumbPath = (uid: string, id: string) => `${assetPath(uid, id)}/assetThumbs/current`;
const filesPath = (uid: string, id: string) => `${assetPath(uid, id)}/assetFiles`;
const filePath = (uid: string, id: string, version: number) => `${filesPath(uid, id)}/${versionId(version)}`;

interface PartDoc extends Part {
  version: number;
  at: number;
}

interface FileDoc {
  version: number;
  contentType: string;
  size: number;
  /** The files port's reference; absent while the bytes are still going up. */
  file?: string;
}

interface VersionDoc {
  version: number;
  parts: number;
  size: number;
  note: string;
  at: number;
  by: string;
}

export interface Library {
  folders: FolderRow[];
  assets: AssetRow[];
  /** Bytes held by rows of kinds this build does not know (made by a newer one): hidden, still counted. */
  foreignBytes?: number;
}

/**
 * The library's two small collections as one view. Calls `cb` once both have
 * answered, then on every change to either.
 */
export function watchLibrary(uid: string, cb: (lib: Library) => void, onError: (err: unknown) => void): Unsubscribe {
  let folders: FolderRow[] | null = null;
  let assets: AssetRow[] | null = null;
  let foreignBytes = 0;
  const emit = () => folders && assets && cb({ folders, assets, foreignBytes });
  const offFolders = backend.data.watchCollection<FolderDoc>(
    foldersPath(uid),
    (read) => {
      folders = read.entries.map((e) => folderFromDoc(e.id, e.data));
      emit();
    },
    onError,
  );
  const offAssets = backend.data.watchCollection<AssetDoc>(
    assetsPath(uid),
    (read) => {
      const known: AssetRow[] = [];
      foreignBytes = 0;
      for (const e of read.entries) {
        const row = assetFromDoc(e.id, e.data);
        if (row) known.push(row);
        else foreignBytes += bytesOfDoc(e.data);
      }
      assets = known;
      emit();
    },
    onError,
  );
  return () => {
    offFolders();
    offAssets();
  };
}

// ── folders ─────────────────────────────────────────────────

/** Write a folder whole: creating, renaming and moving are each this one write. */
export async function writeFolder(uid: string, folder: FolderRow): Promise<void> {
  const { id, ...doc } = folder;
  await backend.data.setDoc(`${foldersPath(uid)}/${id}`, doc);
}

export async function deleteFolder(uid: string, folderId: string): Promise<void> {
  await backend.data.deleteDoc(`${foldersPath(uid)}/${folderId}`);
}

// ── the asset row ───────────────────────────────────────────

const NEWER_KIND = 'That asset was made by a newer version of the app. Reload the page to update it, then try again.';

/**
 * Change an asset row through compare-and-set, so a rename can never put back
 * a version counter another tab just moved. `change` must be pure - it may run
 * more than once. Resolves with the row as written.
 */
export async function changeAsset(
  uid: string,
  id: string,
  change: (row: AssetRow) => AssetRow,
  fresh?: AssetRow,
): Promise<AssetRow> {
  const written = await backend.data.transactDoc<AssetDoc>(assetPath(uid, id), (current) => {
    if (!current && !fresh) return undefined;
    return docFromAsset(change(current ? knownRow(id, current) : (fresh as AssetRow)));
  });
  if (!written) throw new Error('That asset is no longer in your library.');
  return knownRow(id, written);
}

/** One asset's row as it stands on the server now; null when it is gone. A retry's fresh look after `StaleBase`. */
export async function readAsset(uid: string, id: string): Promise<AssetRow | null> {
  const read = await backend.data.getDoc<AssetDoc>(assetPath(uid, id));
  return read.data ? knownRow(id, read.data) : null;
}

/** A stored asset as a row; a kind this build does not know is refused, never rewritten as something it knows. */
function knownRow(id: string, doc: AssetDoc): AssetRow {
  const row = assetFromDoc(id, doc);
  if (!row) throw new Error(NEWER_KIND);
  return row;
}

// ── versions ────────────────────────────────────────────────

export interface SaveRequest {
  uid: string;
  /** The asset to add a version to; for a new one, its row before the first save. */
  asset: AssetRow;
  /** The version's content: the text its kind's `encode` produced. */
  text: string;
  note: Note;
  now: number;
  /**
   * The version the text was built on (the Teams view's rewrites, plan teams-manager): the save is refused with
   * `StaleBase`, writing nothing, when the asset's last whole version is no longer this one. Absent: unguarded.
   * Only a FINISHED save moves it - a save in flight at the same instant is not seen (a dead tab's reservation must
   * never refuse every save after it); its version is still kept, so nothing is lost from the history.
   */
  base?: number;
}

/** A save refused because the asset changed since its text was read: read it again and redo the change. */
export class StaleBase extends Error {
  constructor(name: string) {
    super(`${name} changed since it was read; read it again and retry.`);
    this.name = 'StaleBase';
  }
}

/** Step 1 with the guard: the reservation, unless the row has moved past the base the text was built on. */
function reserveOn(row: AssetRow, base: number | undefined): AssetRow {
  if (movedPast(row, base)) throw new StaleBase(row.name);
  return reserve(row).row;
}

/** What step 2 wrote: the version's size, and how many parts hold it (none for a file). */
interface Written {
  size: number;
  parts: number;
}

/** Steps 1, 3 and 4 around the content's own write, step 2. Resolves with the row as committed. */
async function saveAround(
  { uid, asset, note, now, base }: Omit<SaveRequest, 'text'>,
  write: (version: number) => Promise<Written>,
): Promise<AssetRow> {
  // A guarded save never re-creates an asset deleted since it was read: no `fresh`, so a gone row refuses.
  const reserved = await changeAsset(
    uid,
    asset.id,
    (row) => reserveOn(row, base),
    base === undefined ? asset : undefined,
  );
  const version = reserved.nextVersion - 1;
  const { size, parts } = await write(version);
  const meta: VersionMeta = { version, parts, size, note, at: now, by: uid };
  await backend.data.setDoc(`${versionsPath(uid, asset.id)}/${versionId(version)}`, {
    ...meta,
    note: JSON.stringify(note),
  });
  return changeAsset(uid, asset.id, (row) => commit(row, meta));
}

/** Save a new version, never overwriting one. Resolves with the row as committed. */
export function saveVersion(req: SaveRequest): Promise<AssetRow> {
  const { uid, asset, text, now } = req;
  return saveAround(req, async (version) => {
    const slices = splitText(text);
    // One part at a time, not one batch: a batch is one request, and nine 600 kB
    // parts would pass Firestore's 10 MiB a commit.
    for (const [part, data] of slices.entries()) {
      await backend.data.setDoc(`${partsPath(uid, asset.id)}/${partId(version, part)}`, {
        version,
        parts: slices.length,
        part,
        data,
        at: now,
      });
    }
    return { size: text.length, parts: slices.length };
  });
}

export interface FileSaveRequest extends Omit<SaveRequest, 'text'> {
  blob: Blob;
}

/** Save an image's or a handout's bytes as a new version. A file version has no parts. */
export function saveFileVersion(req: FileSaveRequest): Promise<AssetRow> {
  const { uid, asset, blob } = req;
  return saveAround(req, async (version) => {
    const path = filePath(uid, asset.id, version);
    await backend.data.setDoc(path, { version, contentType: blob.type, size: blob.size });
    const name = `${versionId(version)}.${extensionFor(blob.type)}`;
    const file = await backend.files.upload(path, 'file', blob, { name, contentType: blob.type });
    await backend.data.setDoc(path, { file }, { merge: true });
    return { size: blob.size, parts: 0 };
  });
}

/** A URL the browser can load now for one version's file. */
export async function fileUrl(uid: string, assetId: string, version: number): Promise<string> {
  const path = filePath(uid, assetId, version);
  const read = await backend.data.getDoc<FileDoc>(path);
  if (!read.data?.file) throw new Error(`Version ${version} has no file on the server.`);
  return backend.files.url(path, 'file', read.data.file);
}

/** One version's file, as bytes: for a copy, a restore, a duplicate, an export. */
export async function readFileVersion(uid: string, assetId: string, version: number): Promise<Blob> {
  const res = await fetch(await fileUrl(uid, assetId, version));
  if (!res.ok) throw new Error(`Version ${version}'s file could not be read (the server said ${res.status}).`);
  return res.blob();
}

/** Delete the files of the versions `doomed` picks, bytes first, then their documents. */
async function deleteFiles(uid: string, assetId: string, doomed: (version: number) => boolean): Promise<void> {
  const docs = (await backend.data.listDocs<FileDoc>(filesPath(uid, assetId))).filter((d) => doomed(d.data.version));
  // The bytes before the documents: on Firebase they are Storage objects nothing else would ever delete.
  await Promise.all(
    docs.flatMap((d) =>
      d.data.file ? [backend.files.delete(`${filesPath(uid, assetId)}/${d.id}`, 'file', d.data.file)] : [],
    ),
  );
  if (docs.length) {
    await backend.data.batch(docs.map((d) => ({ op: 'delete', path: `${filesPath(uid, assetId)}/${d.id}` })));
  }
}

/** Every whole version's facts, newest first. Small: the content is not read. */
export async function listVersions(uid: string, assetId: string): Promise<VersionMeta[]> {
  const docs = await backend.data.listDocs<VersionDoc>(versionsPath(uid, assetId));
  return docs.map((d) => versionFromDoc(d.data)).sort((a, b) => b.version - a.version);
}

function versionFromDoc(doc: VersionDoc): VersionMeta {
  let note: Note = { kind: 'library' };
  try {
    note = JSON.parse(doc.note) as Note;
  } catch {
    // A torn note reads as a plain save; the version itself is whole.
  }
  return { version: doc.version, parts: doc.parts ?? 1, size: doc.size, note, at: doc.at, by: doc.by };
}

/** One version's content, joined from its parts. */
export async function readVersion(uid: string, assetId: string, version: number): Promise<string> {
  const facts = await backend.data.getDoc<VersionDoc>(`${versionsPath(uid, assetId)}/${versionId(version)}`);
  if (!facts.data) throw new Error(`Version ${version} is not in your library.`);
  const count = facts.data.parts || 1;
  const parts = await Promise.all(
    Array.from({ length: count }, (_, i) =>
      backend.data.getDoc<PartDoc>(`${partsPath(uid, assetId)}/${partId(version, i)}`),
    ),
  );
  const text = joinParts(parts.flatMap((p) => (p.data ? [p.data] : [])));
  if (text === null) throw new Error(`Version ${version} is incomplete on the server. Try again in a moment.`);
  return text;
}

/** Bring an old version back as the NEWEST one: nothing is overwritten. */
export async function restoreVersion(uid: string, asset: AssetRow, version: number, now: number): Promise<AssetRow> {
  const text = await readVersion(uid, asset.id, version);
  return saveVersion({ uid, asset, text, note: { kind: 'restored', from: version }, now });
}

/** Delete every version but the current one, with its parts. */
export async function pruneVersions(uid: string, asset: AssetRow): Promise<AssetRow> {
  const metas = await listVersions(uid, asset.id);
  const old = metas.filter((m) => m.version !== asset.version);
  if (isFileKind(asset.kind)) await deleteFiles(uid, asset.id, (v) => v !== asset.version);
  await backend.data.batch(old.flatMap((m) => versionOps(uid, asset.id, m)));
  return changeAsset(uid, asset.id, pruned);
}

function versionOps(uid: string, assetId: string, meta: Pick<VersionMeta, 'version' | 'parts'>) {
  return [
    { op: 'delete' as const, path: `${versionsPath(uid, assetId)}/${versionId(meta.version)}` },
    ...Array.from({ length: meta.parts }, (_, i) => ({
      op: 'delete' as const,
      path: `${partsPath(uid, assetId)}/${partId(meta.version, i)}`,
    })),
  ];
}

/** Take the parts of saves that never finished, once no live save can still be writing them. */
export async function reapOrphans(uid: string, assetId: string, now: number): Promise<number> {
  const [metas, parts] = await Promise.all([
    listVersions(uid, assetId),
    backend.data.listDocs<PartDoc>(partsPath(uid, assetId)),
  ]);
  const stale = new Set(
    orphanVersions(
      metas,
      parts.map((p) => p.data),
      now,
    ),
  );
  const doomed = parts.filter((p) => stale.has(p.data.version));
  if (doomed.length) {
    await backend.data.batch(doomed.map((p) => ({ op: 'delete', path: `${partsPath(uid, assetId)}/${p.id}` })));
  }
  return stale.size;
}

/** Delete an asset for good: its row, every version, every part, every file and its picture. */
export async function deleteForever(uid: string, assetId: string): Promise<void> {
  await deleteFiles(uid, assetId, () => true);
  const parts = await backend.data.listDocs<PartDoc>(partsPath(uid, assetId));
  const versions = await backend.data.listDocs<VersionDoc>(versionsPath(uid, assetId));
  await backend.data.batch([
    ...parts.map((p) => ({ op: 'delete' as const, path: `${partsPath(uid, assetId)}/${p.id}` })),
    ...versions.map((v) => ({ op: 'delete' as const, path: `${versionsPath(uid, assetId)}/${v.id}` })),
    { op: 'delete', path: thumbPath(uid, assetId) },
    { op: 'delete', path: assetPath(uid, assetId) },
  ]);
}

// ── the card's picture ──────────────────────────────────────

/** Store the card's picture for a version, then point the row at it. */
export async function writeThumb(uid: string, assetId: string, version: number, data: string): Promise<void> {
  await backend.data.setDoc(thumbPath(uid, assetId), { version, data });
  await changeAsset(uid, assetId, (row) => ({ ...row, thumbVersion: Math.max(row.thumbVersion, version) }));
}

/** The card's picture as a data URL, or null when it has none. */
export async function readThumb(uid: string, assetId: string): Promise<string | null> {
  const read = await backend.data.getDoc<{ data?: string }>(thumbPath(uid, assetId));
  return read.data?.data || null;
}
